import { config } from '../config/env';
import { ConsentApp } from '../api/consentApps';

const ADMIN_CONSENT_AUTHORITY = 'https://login.microsoftonline.com/common/adminconsent';

/**
 * The V2 backend rejects non-GUID state values (`SignUpValidationParams.State`
 * is a `Guid`), so the consent step index cannot live in `state`. Instead the
 * frontend keeps it in sessionStorage, keyed by the invitation id.
 */
const STEP_STORAGE_KEY = 'mnspro.signup.consentStep';

/**
 * The consent-app list is no longer hard-coded; it's fetched from the backend
 * (`GET /api/v2/signUp/consentApps/{invitationId}`) at the start of the flow.
 * Because each consent step is a full-page redirect to Azure AD and back, the
 * fetched list is cached in sessionStorage so the callback can resume with the
 * same ordered chain. Re-fetching mid-chain would be wrong: the backend prunes
 * already-consented apps for a connected tenant, which would shrink the list.
 */
const APPS_STORAGE_KEY = 'mnspro.signup.consentApps';

/**
 * Tracks the single automatic retry we attempt when Azure AD reports
 * `AADSTS650051` (service-principal name already reserved). This happens when
 * an app was removed from the tenant and re-consent is then attempted: a fresh
 * immediate re-consent usually heals it on Azure's side. We remember which
 * (invitation, step) we already auto-retried so a *second* identical failure
 * stops the loop and shows the admin a manual Azure-portal instruction instead.
 */
const RETRY_STORAGE_KEY = 'mnspro.signup.consentRetry';

/**
 * Counts how often we already auto-retried a consent step after a *transient*
 * Azure AD failure (`AADSTS90092`, see `RegisterCallbackPage`). Unlike the
 * `AADSTS650051` marker above this is a counter, not a flag, because we grant
 * more than one attempt with growing delays. It lives in its own key so the two
 * retry mechanisms cannot reset each other's bookkeeping.
 */
const TRANSIENT_RETRY_STORAGE_KEY = 'mnspro.signup.consentTransientRetry';

interface StoredApps {
  invitationId: string;
  apps: ConsentApp[];
}

/** Persists the fetched consent-app chain for the current invitation. */
export const storeConsentApps = (invitationId: string, apps: ConsentApp[]) => {
  window.sessionStorage.setItem(
    APPS_STORAGE_KEY,
    JSON.stringify({ invitationId, apps } satisfies StoredApps),
  );
};

/**
 * Reads the cached consent-app chain, but only when it belongs to the given
 * invitation. Returns `null` when nothing is stored (e.g. a callback hit in a
 * fresh browser session) so the caller can re-fetch.
 */
export const readConsentApps = (invitationId: string): ConsentApp[] | null => {
  try {
    const raw = window.sessionStorage.getItem(APPS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredApps>;
    if (parsed.invitationId !== invitationId || !Array.isArray(parsed.apps)) {
      return null;
    }
    return parsed.apps as ConsentApp[];
  } catch {
    return null;
  }
};

interface StoredStep {
  invitationId: string;
  stepIndex: number;
}

const readStoredStep = (): StoredStep | null => {
  try {
    const raw = window.sessionStorage.getItem(STEP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredStep>;
    if (typeof parsed.invitationId !== 'string' || typeof parsed.stepIndex !== 'number') {
      return null;
    }
    return { invitationId: parsed.invitationId, stepIndex: parsed.stepIndex };
  } catch {
    return null;
  }
};

const writeStoredStep = (value: StoredStep) => {
  window.sessionStorage.setItem(STEP_STORAGE_KEY, JSON.stringify(value));
};

/** Clears the whole consent-flow session state (step index + cached apps). */
export const clearStoredStep = () => {
  window.sessionStorage.removeItem(STEP_STORAGE_KEY);
  window.sessionStorage.removeItem(APPS_STORAGE_KEY);
};

interface StoredRetry {
  invitationId: string;
  stepIndex: number;
}

/**
 * Reads the marker for the consent step we already auto-retried once after an
 * `AADSTS650051`. Returns `null` when no retry is in flight.
 */
export const readConsentRetry = (): StoredRetry | null => {
  try {
    const raw = window.sessionStorage.getItem(RETRY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredRetry>;
    if (typeof parsed.invitationId !== 'string' || typeof parsed.stepIndex !== 'number') {
      return null;
    }
    return { invitationId: parsed.invitationId, stepIndex: parsed.stepIndex };
  } catch {
    return null;
  }
};

/** Records that we are auto-retrying the given consent step exactly once. */
export const markConsentRetry = (value: StoredRetry) => {
  window.sessionStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify(value));
};

/** Clears the auto-retry marker (on success or after the retry also failed). */
export const clearConsentRetry = () => {
  window.sessionStorage.removeItem(RETRY_STORAGE_KEY);
};

export interface StoredTransientRetry {
  invitationId: string;
  stepIndex: number;
  /** Number of automatic retries already spent on this step. */
  attempt: number;
}

/**
 * Reads how many transient-error retries the given consent step has already
 * used. Returns `0` when nothing is stored or the marker belongs to a different
 * invitation or step — a transient failure on another step starts counting from
 * scratch.
 */
export const readTransientRetryAttempt = (invitationId: string, stepIndex: number): number => {
  try {
    const raw = window.sessionStorage.getItem(TRANSIENT_RETRY_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as Partial<StoredTransientRetry>;
    if (
      parsed.invitationId !== invitationId ||
      parsed.stepIndex !== stepIndex ||
      typeof parsed.attempt !== 'number'
    ) {
      return 0;
    }
    return parsed.attempt;
  } catch {
    return 0;
  }
};

/** Records that another transient-error retry is being started for this step. */
export const markTransientRetry = (value: StoredTransientRetry) => {
  window.sessionStorage.setItem(TRANSIENT_RETRY_STORAGE_KEY, JSON.stringify(value));
};

/** Clears the transient-retry counter (on success or once the budget is used up). */
export const clearTransientRetry = () => {
  window.sessionStorage.removeItem(TRANSIENT_RETRY_STORAGE_KEY);
};

export interface ConsentStepContext {
  invitationId: string;
  /** Zero-based index of the next consent step that still needs to run. */
  nextStepIndex: number;
}

/**
 * Reconstruct which consent step a callback belongs to. Falls back to step 1
 * (i.e. step 0 just finished) when nothing is stored — that matches a first
 * callback after a freshly-opened browser session.
 */
export const resolveConsentStep = (stateGuid: string): ConsentStepContext | null => {
  const stored = readStoredStep();
  if (stored && stored.invitationId === stateGuid) {
    return {
      invitationId: stored.invitationId,
      nextStepIndex: stored.stepIndex,
    };
  }
  if (!stateGuid) return null;
  return { invitationId: stateGuid, nextStepIndex: 1 };
};

const buildRegisterCallbackUrl = (): string => {
  // Allow ENV-driven override for cases where window.location can't match
  // the URI registered on the Azure AD app (e.g. local dev against an
  // AAD app that only has the production URI registered).
  if (config.signUpRedirectUri) return config.signUpRedirectUri;
  const { protocol, host } = window.location;
  // Path casing must match the URI registered on the Azure AD app. The
  // legacy PortalFrontend used the MVC action name `Register` (capital R)
  // and that's what's registered for the MNSproCloud + Exchange apps.
  return `${protocol}//${host}/signup/Register`;
};

const buildAdminConsentRedirect = (clientId: string, invitationId: string): string => {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: buildRegisterCallbackUrl(),
    response_mode: 'query',
    // The V2 backend requires `state` to be a Guid — pass the invitation id
    // verbatim and track the step index in sessionStorage.
    state: invitationId,
  });
  return `${ADMIN_CONSENT_AUTHORITY}?${params.toString()}`;
};

/**
 * Returns the URL the browser must navigate to in order to start the consent
 * step at `nextStepIndex`, or `null` when all consents have been granted.
 * Side-effect: persists the new step index in sessionStorage so the next
 * callback can resume after the redirect.
 *
 * `apps` is the (cached) consent-app chain fetched from the backend.
 */
export const getNextConsentStepUrl = (
  apps: ConsentApp[],
  invitationId: string,
  nextStepIndex: number,
): string | null => {
  const next = apps[nextStepIndex];
  if (!next) return null;
  writeStoredStep({ invitationId, stepIndex: nextStepIndex + 1 });
  return buildAdminConsentRedirect(next.id, invitationId);
};
